import { useState, FC } from 'react';
import Alert from '@mui/material/Alert';
import { useSnackbar } from 'notistack';
import { Box, Fade, ButtonGroup, Button, Typography } from '@mui/material';
import { Close, Delete } from '@mui/icons-material';
import { Modal, Loader } from '../../../components';
import { IBuild } from '../../../models';
import { deleteBuild, softDeleteBuild } from '../../../fetch';

interface IDeleteBuildModal {
  currentBuild: IBuild;
  open: boolean;
  onClose: () => void;
  fetchBuilds: () => void;
}

export const DeleteBuildModal: FC<IDeleteBuildModal> = ({
  open,
  onClose,
  currentBuild,
  fetchBuilds
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleting, setDeleting] = useState(false);
  const isHardDelete = currentBuild?.alert?.includes('Pending Delete');
  return (
    <Modal open={open} onClose={onClose} maxWidth="sm">
      <Fade in={open}>
        <div>
          {isDeleting && (
            <Loader position="centered" type="overlay">
              Deleting...
            </Loader>
          )}
          <Box marginBottom="1rem">
            {currentBuild && (
              <Typography variant="h5">
                {currentBuild?.jobType ? `Delete ${currentBuild.jobType} Build` : 'Delete Build'}{' '}
                {currentBuild?.neighborhood} - Lot {currentBuild?.lotNumber}{' '}
                {currentBuild?.status ? `- ${currentBuild?.status}` : ''}{' '}
                {currentBuild?.buyer ? `- ${currentBuild?.buyer}` : ''}
              </Typography>
            )}
          </Box>
          <Alert severity="warning">
            {isHardDelete
              ? 'This will skip the process of pending deletion. Are you sure you want to fully delete this build?'
              : `This function will reset the job type to Lots Under Contract and completely delete a
            build, all build documents, options etc. from the database. Use this function when a
            build was created in error or when you are certain the buyer will not complete the
            contract. Are you sure you want to delete this build?`}
          </Alert>
          <Box mt={1} display="flex" alignItems="center" justifyContent="flex-end">
            <ButtonGroup>
              <Button color="inherit" variant="contained" startIcon={<Close />} onClick={onClose}>
                Cancel
              </Button>
              <Button
                id="save"
                color="error"
                variant="contained"
                disabled={isDeleting}
                startIcon={<Delete />}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    if (isHardDelete) {
                      await deleteBuild(currentBuild.buildId, 'lotsundercontract');
                    } else {
                      await softDeleteBuild(currentBuild.buildId);
                    }
                    enqueueSnackbar(`Build deleted!`, {
                      variant: 'success'
                    });
                    onClose();
                    fetchBuilds();
                  } catch (error) {
                    enqueueSnackbar('Error deleting build, please try again.', {
                      variant: 'error'
                    });
                  } finally {
                    setDeleting(false);
                  }
                }}
              >
                Delete
              </Button>
            </ButtonGroup>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
};
